// Builds for game portals (CrazyGames, itch.io) run inside the portal's iframe on the portal's
// domain. REACT_APP_PORTAL is set by scripts/build-portal.mjs; on myfootballgame.com it is unset.
//
// Portal rules we follow: play starts instantly as a guest (no signup wall), no real-money
// purchases outside the portal, no links out to other sites, no Google sign-in popup.
export const PORTAL = process.env.REACT_APP_PORTAL || null;
export const isPortal = !!PORTAL;
export const isCrazyGames = PORTAL === 'crazygames';

// Feature switches used by components.
export const portalFlags = {
  realMoneyStore: !isPortal,   // Stripe checkout is not allowed inside portals
  externalLinks: !isPortal,    // Discord/social links, share-to-WhatsApp, links to our site
  googleSignIn: !isPortal,     // popups from inside a portal iframe are blocked or not allowed
  pushNotifications: !isPortal,
  logout: !isPortal,           // a guest who logs out would lose their club
  // CrazyGames Basic Launch: "No external login options", so no save-with-email prompt there.
  saveClubPrompt: !isCrazyGames,
};

// Static files referenced from code (e.g. the crest fallback) must be relative inside a portal.
export const asset = (path) => `${process.env.PUBLIC_URL || ''}${path}`;

// --- CrazyGames SDK v3 (script tag injected into the CrazyGames build only) --------------------
// Every call is a safe no-op when the SDK is missing (our site, itch.io) or not initialised.
let sdk = null;
let ready = null;

export function initPortalSdk() {
  if (!isCrazyGames) return Promise.resolve(null);
  if (ready) return ready;
  ready = (async () => {
    try {
      const s = window.CrazyGames?.SDK;
      if (!s) return null;
      await s.init();
      sdk = s;
      return s;
    } catch {
      return null;
    }
  })();
  return ready;
}

const call = (fn) => { try { if (sdk) fn(sdk); } catch { /* never let the SDK break the game */ } };

// CrazyGames Data Module: progress (our club token) follows a logged-in CrazyGames player across
// devices; for anonymous players it falls back to the browser. Keys are ours, values are strings.
export const portalStorage = {
  get: (key) => { try { return sdk?.data?.getItem(key) ?? null; } catch { return null; } },
  set: (key, value) => { try { sdk?.data?.setItem(key, value); } catch { /* ignore */ } },
};

export const portalSdk = {
  loadingStart: () => call((s) => s.game.loadingStart()),
  loadingStop: () => call((s) => s.game.loadingStop()),
  gameplayStart: () => call((s) => s.game.gameplayStart()),
  gameplayStop: () => call((s) => s.game.gameplayStop()),
  happytime: () => call((s) => s.game.happytime()),
  // Between matches only. CrazyGames rate-limits midgame ads itself (and disables ads during
  // Basic Launch), so calling this after every match is fine. Resolves when the game can continue.
  midgameAd: () => new Promise((resolve) => {
    if (!sdk) return resolve();
    try {
      sdk.ad.requestAd('midgame', { adStarted: () => {}, adFinished: () => resolve(), adError: () => resolve() });
    } catch {
      resolve();
    }
  }),
};
